import React, { useCallback, useState } from "react";
import { useBlocker } from "react-router-dom";
import Modal from "react-modal";
import { Button } from "components/ui";
import {
  FiAlertTriangle,
  FiCheck,
  FiCornerDownLeft,
  FiX,
} from "react-icons/fi";
import { ImSpinner9 } from "react-icons/im";
import useBeforeUnload from "utils/hooks/useBeforeUnload";

const SaveDraftNavigationWarning = ({
  when,
  onSaveDraft,
  onLeaveWithoutSaving,
}) => {
  const blocker = useBlocker(useCallback(() => when, [when]));
  const [saving, setSaving] = useState(false);

  const isOpen = blocker.state === "blocked";

  useBeforeUnload(when);

  const handleStay = useCallback(() => {
    if (saving) return;

    blocker.reset?.();
  }, [blocker, saving]);

  const handleLeaveWithoutSaving = useCallback(async () => {
    if (saving) return;

    setSaving(true);
    try {
      await onLeaveWithoutSaving?.();
      blocker.proceed?.();
    } catch (error) {
      console.error("Failed to discard request:", error);
    } finally {
      setSaving(false);
    }
  }, [blocker, onLeaveWithoutSaving, saving]);

  const handleSaveDraft = useCallback(async () => {
    if (saving) return;

    setSaving(true);

    try {
      await onSaveDraft?.();
      blocker.reset?.();
    } catch (error) {
      console.error("Failed to save draft:", error);
      blocker.reset?.();
    } finally {
      setSaving(false);
    }
  }, [blocker, onSaveDraft, saving]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleStay}
      shouldCloseOnOverlayClick={!saving}
      shouldCloseOnEsc={!saving}
      ariaHideApp={false}
      closeTimeoutMS={150}
      className="relative w-[560px] max-w-[94vw] bg-transparent outline-none"
      overlayClassName="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80"
    >
      <div className="relative flex w-full flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white px-6 pb-4 pt-7 text-center text-black shadow-2xl dark:border-white/5 dark:bg-[#111114] dark:text-white">
        <button
          type="button"
          onClick={handleStay}
          disabled={saving}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-gray-100 text-gray-600 transition hover:bg-gray-200 hover:text-black disabled:opacity-50 dark:border-white/10 dark:bg-white/5 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white"
        >
          <FiX size={13} />
        </button>

        <div className="mx-auto mb-3 flex h-[52px] w-[52px] items-center justify-center rounded-full border border-amber-500/40 bg-amber-500/10">
          <FiAlertTriangle size={22} className="text-amber-500" />
        </div>

        <h3 className="mb-2 text-xl font-bold text-black dark:text-white">
          Leave without saving?
        </h3>

        <p className="mx-auto max-w-[460px] text-sm leading-normal text-gray-700 dark:text-gray-300">
          You have unsaved changes on this request. If you leave this page now,
          your progress will be lost.
        </p>

        <p className="mt-2 text-xs text-gray-600 dark:text-gray-500">
          Would you like to save this request as a draft before you go?
        </p>

        <div className="mx-auto mt-10 grid w-full grid-cols-[1.45fr_1fr_1fr] gap-3">
          <Button
            type="button"
            onClick={handleStay}
            disabled={saving}
            className="!flex !h-[48px] w-full items-center justify-center gap-1.5 !whitespace-nowrap !rounded-md !border !border-[#16A34A] !bg-transparent !px-2 !text-[11px] !font-semibold !leading-4 !text-[#16A34A] hover:!bg-[#16A34A]/10"
          >
            <FiCornerDownLeft size={12} />
            <span>Stay &amp; Continue This Request</span>
          </Button>

          <Button
            type="button"
            onClick={handleLeaveWithoutSaving}
            disabled={saving}
            className="!flex !h-[48px] w-full items-center justify-center gap-1.5 !whitespace-nowrap !rounded-md !border !border-gray-300 !bg-white !px-3 !text-[11px] !font-semibold !leading-4 !text-black hover:!bg-gray-100 dark:!border-white/10 dark:!bg-[#17171b] dark:!text-white dark:hover:!bg-white/10"
          >
            <FiX size={12} />
            <span>No, Don&apos;t Save</span>
          </Button>

          <Button
            type="button"
            variant="solid"
            onClick={handleSaveDraft}
            disabled={saving}
            className="!flex !h-[48px] w-full items-center justify-center gap-1.5 !whitespace-nowrap !rounded-md !border-0 !px-3 !text-[11px] !font-bold !leading-4 !text-white"
          >
            {saving ? (
              <ImSpinner9 className="animate-spin" size={12} />
            ) : (
              <FiCheck size={12} />
            )}
            <span>Yes, Save as Draft</span>
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SaveDraftNavigationWarning;
